/**
 * Analytics API — overview, funnel, timeToHire, vacancyStats, activityTimeline.
 *
 * Все пути используют префикс `/reports/...` вместо `/analytics/...`:
 * EasyPrivacy / uBlock / Brave Shields блокируют любые URL с подстрокой
 * "/analytics/" на стороне браузера. Backend регистрирует ТОТ ЖЕ роутер
 * под обоими префиксами (см. backend/app/api/router.py).
 */

import { apiFetch } from './core'

/**
 * Строит query-строку периода для аналитики.
 * Поддерживает:
 *   - пресет: periodQuery(90)                       → "days=90"
 *   - произвольный период: periodQuery({ dateFrom, dateTo }) → "date_from=…&date_to=…"
 * Если для произвольного периода заполнена только одна дата — fallback на пресет
 * (либо переданный `fallbackDays`), чтобы не слать неполный диапазон (backend → 422).
 */
function periodQuery(period, fallbackDays = 90) {
    if (period && typeof period === 'object') {
        const { dateFrom, dateTo } = period
        if (dateFrom && dateTo) {
            return `date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`
        }
        return `days=${fallbackDays}`
    }
    const days = Number(period) || fallbackDays
    return `days=${days}`
}

export const analytics = {
    overview: (days = 30) => apiFetch(`/reports/overview?days=${days}`),
    funnel: (days = 30) => apiFetch(`/reports/funnel?days=${days}`),
    timeToHire: () => apiFetch('/reports/time-to-hire'),
    vacancyStats: () => apiFetch('/reports/vacancy-stats'),
    activityTimeline: (days = 7) => apiFetch(`/reports/activity-timeline?days=${days}`),
    ceoSummary: () => apiFetch('/reports/ceo-summary'),
    aiInsights: () => apiFetch('/reports/ai-strategic-insights'),
    headcountHistory: (params = {}) => {
        const q = new URLSearchParams(params).toString()
        return apiFetch(`/reports/headcount-history${q ? `?${q}` : ''}`)
    },
    // period: число дней (пресет) ИЛИ { dateFrom, dateTo } (произвольный период).
    sourceROI: (period = 90) => apiFetch(`/reports/source-roi?${periodQuery(period)}`),
    stageConversion: (period = 90) => apiFetch(`/reports/stage-conversion?${periodQuery(period)}`),
}

export const succession = {
    dashboard: () => apiFetch('/succession/dashboard'),
    listPlans: () => apiFetch('/succession/plans'),
    createPlan: (data) => apiFetch('/succession/plans', { method: 'POST', body: JSON.stringify(data) }),
    updatePlan: (id, data) => apiFetch(`/succession/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deletePlan: (id) => apiFetch(`/succession/plans/${id}`, { method: 'DELETE' }),
    addCandidate: (planId, data) => apiFetch(`/succession/plans/${planId}/candidates`, { method: 'POST', body: JSON.stringify(data) }),
    updateCandidate: (planId, candidateId, data) => apiFetch(`/succession/plans/${planId}/candidates/${candidateId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    removeCandidate: (planId, candidateId) => apiFetch(`/succession/plans/${planId}/candidates/${candidateId}`, { method: 'DELETE' }),
    recalculate: (planId, candidateId) => apiFetch(`/succession/plans/${planId}/candidates/${candidateId}/recalculate`, { method: 'POST' }),
}
